import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  createFilterOptions,
  TextField,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Slide,
  Stack,
  Typography,
  Tooltip,
  Box,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import api from '../../services/api';
import { COMPACT_DATE_TIME_FORMAT, formatFloatToTimeHM } from '../../utils/timeFormatter';
import NewActionItemForm from './NewActionItemForm';

const filterOptions = createFilterOptions({ stringify: (option) => `${option.description} ${option.task_name}` });

// The dialog slides in from the right as a half-screen panel.
const SlideLeft = React.forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);

/**
 * "Sep 24, 10:00 - 10:20": the end drops the date when it is the same day.
 * @param {Object} note
 * @returns {string}
 */
const formatNoteRange = (note) => {
  if (!note.start_time) return '';
  const start = dayjs(note.start_time);
  const end = note.end_time ? dayjs(note.end_time) : null;
  const endText = end ? end.format(end.isSame(start, 'day') ? 'HH:mm' : COMPACT_DATE_TIME_FORMAT) : '?';
  return `${start.format(COMPACT_DATE_TIME_FORMAT)} - ${endText}`;
};

const sumHours = (notes) => notes.reduce((sum, note) => sum + (parseFloat(note.duration) || 0), 0);

/**
 * Colour-coded AI confidence (0-100). ``manual`` marks a user override.
 * @param {Object} props
 * @param {number|string|null} props.confidence
 * @param {boolean} [props.manual]
 * @param {string} [props.reasoning] - Shown as a tooltip
 */
export const ConfidenceChip = ({ confidence, manual = false, reasoning = '' }) => {
  if (manual) return <Chip label="Manual" size="small" variant="outlined" />;
  if (confidence === null || confidence === undefined || confidence === '') return null;
  const value = Math.round(parseFloat(confidence));
  const color = value >= 80 ? 'success' : value >= 50 ? 'warning' : 'error';
  return (
    <Tooltip title={reasoning}>
      <Chip label={`${value}%`} size="small" color={color} variant="outlined" />
    </Tooltip>
  );
};

/**
 * Action item search for one note. Its last option always opens the inline
 * "new action item" form, so there is a way out when nothing fits.
 * @param {Object} props
 * @param {Object[]} props.options
 * @param {Object|null} props.value
 * @param {Function} props.onPick - With the chosen option
 * @param {Function} props.onCreate - With the typed text
 * @param {boolean} [props.autoFocus]
 */
const ActionItemPicker = ({ options, value, onPick, onCreate, autoFocus = false }) => (
  <Autocomplete
    size="small"
    options={options}
    groupBy={(option) => (option.create ? '' : option.isNew ? 'New action items' : option.task_name || '')}
    getOptionLabel={(option) => option.description || ''}
    isOptionEqualToValue={(option, selected) => option.id === selected.id}
    filterOptions={(opts, params) => [
      ...filterOptions(opts, params),
      { create: true, id: 'create', description: '', inputValue: params.inputValue },
    ]}
    value={value}
    onChange={(_, option) => (option?.create ? onCreate(option.inputValue.trim()) : option && onPick(option))}
    openOnFocus
    renderOption={(props, option) =>
      option.create ? (
        <li {...props} key="create">
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          {option.inputValue.trim() ? `Create "${option.inputValue.trim()}"...` : 'Create new action item...'}
        </li>
      ) : (
        <li {...props} key={option.id}>
          {option.description}
          {option.isNew && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {option.task_name}
            </Typography>
          )}
        </li>
      )
    }
    renderInput={(params) => (
      <TextField {...params} autoFocus={autoFocus} placeholder="Search action items or create one" />
    )}
  />
);

/**
 * Half-screen panel that streams AI matches for pending notes and shows them
 * grouped task -> action item -> notes, with the time each item already has
 * and the time these notes add. Notes can be re-pointed, or attached to a new
 * action item / task, in place. Ticked notes are saved as time logs; Cancel
 * discards everything (drafted items included).
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Object[]} props.notes - Pending notes to link
 * @param {number} props.workspaceId
 * @param {Function} props.onClose - Cancel
 * @param {Function} props.onSaved - After a successful save, with the linked note ids
 */
const NoteLinkDialog = ({ open, notes, workspaceId, onClose, onSaved }) => {
  // One row per note: { state: 'loading'|'done'|'error', aiItemId, confidence, reasoning, selected, checked }
  const [rows, setRows] = useState(() =>
    Object.fromEntries(notes.map((n) => [n.id, { state: 'loading', selected: null, checked: false }]))
  );
  const [options, setOptions] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Drafted in NewActionItemForm; only created on Save. Draft items are picker
  // options ({id: key, isNew}); draft tasks can hold several items.
  const [draftItems, setDraftItems] = useState([]);
  const [draftTasks, setDraftTasks] = useState([]);
  const nextDraftId = useRef(1);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null); // { noteId, description }
  const [streaming, setStreaming] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Connecting notes to action items...');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const abortRef = useRef(null);

  // Picker options: the user's action items, flattened with their task.
  useEffect(() => {
    if (!workspaceId) return;
    api
      .get('/grouped-action-items/', { params: { workspace_id: workspaceId } })
      .then((response) => {
        const payload = response.data.payload || [];
        setTasks(
          payload.map((task) => ({
            id: task.task_id,
            name: task.task_name,
            context: [task.project_name, task.track_name].filter(Boolean).join(' · '),
          }))
        );
        setOptions(
          payload.flatMap((task) =>
            (task.action_items || []).map((item) => ({
              id: item.id,
              description: item.description,
              time_taken: parseFloat(item.time_taken) || 0,
              task_id: task.task_id,
              task_name: task.task_name,
            }))
          )
        );
      })
      .catch((err) => console.error('Error fetching action items:', err));
  }, [workspaceId]);

  const applyEvent = (event) => {
    switch (event.type) {
      case 'status':
        setStatusMessage(event.message);
        break;
      case 'match':
        setRows((prev) => ({
          ...prev,
          [event.note_id]: {
            state: 'done',
            aiItemId: event.action_item_id,
            confidence: event.confidence,
            reasoning: event.reasoning,
            selected: event.action_item_id
              ? { id: event.action_item_id, description: event.action_item, task_name: event.task_name }
              : null,
            // Pre-ticked when the AI found something; the user unticks what they disagree with.
            checked: Boolean(event.action_item_id),
          },
        }));
        break;
      case 'error':
        setError(event.message || 'Could not link some notes.');
        setRows((prev) => {
          const next = { ...prev };
          (event.note_ids || []).forEach((id) => {
            next[id] = { ...(next[id] || {}), state: 'error', selected: null };
          });
          return next;
        });
        break;
      case 'done':
        if (event.message) setStatusMessage(event.message);
        break;
      default:
        break;
    }
  };

  // Stream matches as NDJSON (same reader as the chatbot).
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const response = await fetch(`${api.defaults.baseURL}time-log-notes/match/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({ workspace_id: workspaceId, note_ids: notes.map((n) => n.id) }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        // Network chunks don't align with NDJSON lines; carry the partial line over.
        let pending = '';
        while (true) {
          const { done, value } = await reader.read();
          const text = done ? pending + decoder.decode() : pending + decoder.decode(value, { stream: true });
          const lines = text.split('\n');
          pending = done ? '' : lines.pop();
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              applyEvent(JSON.parse(line));
            } catch (parseError) {
              console.error('Bad match event:', line, parseError);
            }
          }
          if (done) break;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error linking notes:', err);
        setError('AI linking failed. You can still pick action items by hand.');
      } finally {
        if (!controller.signal.aborted) {
          setStreaming(false);
          // Anything the stream never answered becomes a manual pick.
          setRows((prev) =>
            Object.fromEntries(
              Object.entries(prev).map(([id, row]) => [id, row.state === 'loading' ? { ...row, state: 'error' } : row])
            )
          );
        }
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isApplied = (row) => Boolean(row?.checked && row.selected);
  const appliedNotes = notes.filter((n) => isApplied(rows[n.id]));
  const checkableIds = notes.filter((n) => rows[n.id]?.selected).map((n) => n.id);
  const doneCount = Object.values(rows).filter((r) => r.state !== 'loading').length;

  // Picking an item ticks the row; clearing it unticks.
  const handleSelect = (noteId, selected) => {
    setRows((prev) => ({ ...prev, [noteId]: { ...prev[noteId], selected, checked: Boolean(selected) } }));
    setEditingNoteId(null);
    setCreatingFor(null);
  };

  const setChecked = (noteIds, checked) =>
    setRows((prev) => {
      const next = { ...prev };
      noteIds.forEach((id) => {
        if (next[id]?.selected) next[id] = { ...next[id], checked };
      });
      return next;
    });

  const handleCreateDraft = ({ description, task, newTask }) => {
    const key = `new-item-${nextDraftId.current++}`;
    let item;
    if (newTask) {
      const taskKey = `new-task-${nextDraftId.current++}`;
      setDraftTasks((prev) => [...prev, { key: taskKey, ...newTask }]);
      item = { id: key, isNew: true, description, task_key: taskKey, task_name: newTask.name };
    } else if (task.isNew) {
      item = { id: key, isNew: true, description, task_key: task.id, task_name: task.name };
    } else {
      item = { id: key, isNew: true, description, task_id: task.id, task_name: task.name };
    }
    setDraftItems((prev) => [...prev, item]);
    handleSelect(creatingFor.noteId, item);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const usedDrafts = new Set(appliedNotes.map((note) => rows[note.id].selected).filter((s) => s.isNew));
    try {
      const response = await api.post('/time-log-notes/commit/', {
        workspace_id: workspaceId,
        new_items: [...usedDrafts].map((item) => ({
          key: item.id,
          description: item.description,
          ...(item.task_key
            ? { new_task: draftTasks.find((t) => t.key === item.task_key) }
            : { task_id: item.task_id }),
        })),
        links: appliedNotes.map((note) => {
          const row = rows[note.id];
          return {
            note_id: note.id,
            ...(row.selected.isNew ? { new_item_key: row.selected.id } : { action_item_id: row.selected.id }),
            ai_action_item_id: row.aiItemId ?? null,
            confidence: row.confidence ?? null,
            reasoning: row.reasoning || '',
          };
        }),
      });
      onSaved((response.data.payload || []).map((note) => note.id));
    } catch (err) {
      console.error('Error saving linked notes:', err);
      setError(err.response?.data?.detail || 'Could not save time logs.');
      setSaving(false);
    }
  };

  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  // Make sure the AI's pick is selectable even if the picker list lacks it;
  // drafted items follow so other notes can reuse them.
  const pickerOptions = useMemo(() => {
    const extra = Object.values(rows)
      .map((r) => r.selected)
      .filter((s) => s && !s.isNew && !optionById.has(s.id));
    return [...options, ...new Map(extra.map((s) => [s.id, s])).values(), ...draftItems];
  }, [options, optionById, rows, draftItems]);

  const taskChoices = useMemo(
    () => [...draftTasks.map((t) => ({ id: t.key, name: t.name, isNew: true })), ...tasks],
    [tasks, draftTasks]
  );

  // Notes grouped task -> action item, plus the ones still matching or unmatched.
  const groups = useMemo(() => {
    const matching = [];
    const unassigned = [];
    const byTask = new Map();
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    notes.forEach((note) => {
      const row = rows[note.id] || {};
      if (!row.selected) {
        (row.state === 'loading' ? matching : unassigned).push(note);
        return;
      }
      const item = optionById.get(row.selected.id) || row.selected;
      const taskKey = item.task_key || item.task_id || `name:${item.task_name}`;
      if (!byTask.has(taskKey)) {
        byTask.set(taskKey, {
          key: taskKey,
          name: item.task_name,
          context: taskById.get(item.task_id)?.context || '',
          isNew: Boolean(item.task_key),
          items: new Map(),
        });
      }
      const items = byTask.get(taskKey).items;
      if (!items.has(item.id)) items.set(item.id, { item, notes: [] });
      items.get(item.id).notes.push(note);
    });
    const taskGroups = [...byTask.values()].map((task) => {
      const items = [...task.items.values()];
      return { ...task, items, notes: items.flatMap((entry) => entry.notes) };
    });
    return { matching, unassigned, tasks: taskGroups };
  }, [notes, rows, optionById, tasks]);

  const appliedHours = (groupNotes) => sumHours(groupNotes.filter((n) => isApplied(rows[n.id])));

  /**
   * One note: tick, text, time, confidence and an edit toggle that opens the
   * picker (or the new action item form) right underneath.
   * @param {Object} note
   * @param {boolean} [alwaysPick] - Unmatched notes show the picker straight away
   */
  const renderNote = (note, alwaysPick = false) => {
    const row = rows[note.id] || {};
    const creating = creatingFor?.noteId === note.id;
    const picking = (alwaysPick || editingNoteId === note.id) && !creating;
    const manual = (row.selected?.id ?? null) !== (row.aiItemId ?? null);
    return (
      <Box key={note.id} sx={{ py: 0.75, opacity: row.selected && !row.checked ? 0.55 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={isApplied(row)}
            disabled={!row.selected}
            onChange={(e) => setChecked([note.id], e.target.checked)}
            inputProps={{ 'aria-label': 'Log this note' }}
          />
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {note.notes}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatNoteRange(note)} · {formatFloatToTimeHM(note.duration)}
            </Typography>
          </Box>
          {row.selected && <ConfidenceChip confidence={row.confidence} manual={manual} reasoning={row.reasoning} />}
          {row.selected && (
            <Tooltip title="Change action item">
              <IconButton
                size="small"
                color={editingNoteId === note.id || creating ? 'primary' : 'default'}
                onClick={() => {
                  setCreatingFor(null);
                  setEditingNoteId((id) => (id === note.id ? null : note.id));
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Collapse in={picking} unmountOnExit>
          <Box sx={{ pl: 4.5, pt: 1 }}>
            <ActionItemPicker
              options={pickerOptions}
              value={row.selected || null}
              autoFocus={!alwaysPick}
              onPick={(option) => handleSelect(note.id, option)}
              onCreate={(typed) =>
                setCreatingFor({ noteId: note.id, description: typed || (note.notes || '').trim() })
              }
            />
          </Box>
        </Collapse>
        <Collapse in={creating} unmountOnExit>
          <Box sx={{ pl: 4.5, pt: 1 }}>
            <NewActionItemForm
              initialDescription={creatingFor?.description || ''}
              tasks={taskChoices}
              workspaceId={workspaceId}
              onCancel={() => setCreatingFor(null)}
              onCreate={handleCreateDraft}
            />
          </Box>
        </Collapse>
      </Box>
    );
  };

  const allChecked = checkableIds.length > 0 && checkableIds.every((id) => rows[id].checked);
  const someChecked = checkableIds.some((id) => rows[id].checked);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      TransitionComponent={SlideLeft}
      sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
      PaperProps={{
        sx: {
          m: 0,
          width: { xs: '100%', md: '50vw' },
          maxWidth: 'none',
          height: '100%',
          maxHeight: '100%',
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Box sx={{ flex: 1 }}>Link notes to action items</Box>
        <IconButton aria-label="Close" onClick={handleCancel}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        {streaming && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {statusMessage} ({doneCount} / {notes.length})
            </Typography>
            <LinearProgress
              variant={doneCount ? 'determinate' : 'indeterminate'}
              value={(doneCount / Math.max(notes.length, 1)) * 100}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            disabled={!checkableIds.length}
            onChange={(e) => setChecked(checkableIds, e.target.checked)}
            inputProps={{ 'aria-label': 'Log all matched notes' }}
          />
          <Typography variant="body2">
            <strong>{appliedNotes.length}</strong> of {notes.length} notes ticked ·{' '}
            <strong>{formatFloatToTimeHM(sumHours(appliedNotes))}</strong> to log
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {groups.unassigned.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'warning.main' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Needs an action item ({groups.unassigned.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pick one, or create a new action item or task from the list.
            </Typography>
            {groups.unassigned.map((note) => renderNote(note, true))}
          </Paper>
        )}

        {groups.tasks.map((task) => {
          const taskNoteIds = task.notes.map((n) => n.id);
          const taskChecked = taskNoteIds.filter((id) => rows[id].checked).length;
          const adding = appliedHours(task.notes);
          return (
            <Accordion key={task.key} defaultExpanded disableGutters variant="outlined" sx={{ mb: 1.5, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, pr: 1 }}>
                  <Checkbox
                    size="small"
                    sx={{ p: 0.5 }}
                    checked={taskChecked === taskNoteIds.length}
                    indeterminate={taskChecked > 0 && taskChecked < taskNoteIds.length}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onChange={(e) => setChecked(taskNoteIds, e.target.checked)}
                    inputProps={{ 'aria-label': `Log all notes for ${task.name}` }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {task.name}
                      </Typography>
                      {task.isNew && <Chip label="New task" size="small" color="primary" variant="outlined" />}
                    </Box>
                    {task.context && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {task.context}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {task.notes.length} note{task.notes.length === 1 ? '' : 's'}
                  </Typography>
                  {adding > 0 && (
                    <Chip label={`+${formatFloatToTimeHM(adding)}`} size="small" color="success" variant="outlined" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={1.5}>
                  {task.items.map(({ item, notes: itemNotes }) => {
                    const logged = item.time_taken || 0;
                    const itemAdding = appliedHours(itemNotes);
                    return (
                      <Box key={item.id}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                            {item.description}
                            {item.isNew && (
                              <Chip label="New" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {logged ? `${formatFloatToTimeHM(logged)} logged` : 'Nothing logged yet'}
                            {itemAdding > 0 && (
                              <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {' '}
                                +{formatFloatToTimeHM(itemAdding)}
                              </Box>
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                          {itemNotes.map((note) => renderNote(note))}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {groups.matching.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Finding action items...
            </Typography>
            {groups.matching.map((note) => (
              <Box key={note.id} sx={{ py: 0.75, pl: 4.5 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {note.notes}
                </Typography>
                <Skeleton variant="text" width="40%" />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        {!streaming && appliedNotes.length < notes.length && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {notes.length - appliedNotes.length} unticked note(s) will stay pending.
          </Typography>
        )}
        <Button onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={streaming || saving || !appliedNotes.length}>
          {saving
            ? 'Saving...'
            : appliedNotes.length
              ? `Log ${appliedNotes.length} note${appliedNotes.length === 1 ? '' : 's'}`
              : 'Log notes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteLinkDialog;
